import {
  Button,
  FullPageLoader,
  Squid,
  type SquidItemProps,
  Text,
} from "@sequoiacap/client-ui";
import { FeedbackMailTo } from "~/components/library/FeedbackPrompt";
import { SquidChildWithImage } from "~/components/layout/SquidChildWithImage";
import Head from "next/head";
import classNames from "classnames";
import styles from "./LoginContainer.module.scss";
import useLoginContainer from "./useLoginContainer";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const featuredFounders: Array<{ [key: string]: any }> = [
  {
    ctor: SquidChildWithImage,
    type: "RealTalk",
    name: "What gets great candidates to a yes?",
    backgroundImageURL: "/featured-founders/realtalk2.jpg",
  },
  {
    ctor: SquidChildWithImage,
    name: "Helen Mayer",
    meta: "Co-Founder & CEO, Otter",
    backgroundImageURL: "/featured-founders/helen mayer.jpg",
  },
  {
    ctor: SquidChildWithImage,
    name: "Sanchali Pal",
    meta: "Founder & CEO, Commons",
    backgroundImageURL: "/featured-founders/sanchali pal.jpg",
  },
  {
    ctor: SquidChildWithImage,
    name: "Keller Rinaudo",
    meta: "Co-Founder & CEO, Zipline",
    backgroundImageURL: "/featured-founders/keller rinaudo.jpg",
  },
  {
    ctor: SquidChildWithImage,
    type: "RealTalk",
    name: "How do you scale culture while remote?",
    backgroundImageURL: "/featured-founders/realtalk1.jpg",
  },
  {
    ctor: SquidFutureFounder,
  },
  {
    ctor: SquidChildWithImage,
    name: "Amira Yahyaoui",
    meta: "Founder & CEO, Mos",
    backgroundImageURL: "/featured-founders/amira yahyaoui.jpg",
  },
  {
    ctor: SquidChildWithImage,
    name: "Jacolon Walker",
    meta: "Co-Founder & CTO, Monad",
    backgroundImageURL: "/featured-founders/jacolon walker.jpg",
  },
];

function LoginContainer(): JSX.Element {
  const vm = useLoginContainer();

  if (vm.loading) {
    return <FullPageLoader />;
  }

  return (
    <>
      <Head>
        <title>Ampersand</title>
      </Head>
      <div className={styles.container}>
        <div className={styles.lhs}>
          <Squid className={styles.squid}>
            {featuredFounders.map((item, key) => {
              const { ctor, ...rest } = item;
              return ctor({ ...rest, key });
            })}
          </Squid>
        </div>
        <div className={styles.rhs}>
          <Text
            as="h1"
            variant="subheader1"
            className={styles.title}
            wrap="noWrap"
          >
            Welcome Sequoia Founders
          </Text>
          <Text
            as="p"
            variant="subheader3"
            className={styles.subtitle}
            color="textSupporting"
          >
            Open the vault to five decades of Sequoia
            <br />
            Company-building knowledge
          </Text>
          <div className={styles.loginBtns}>
            <Button
              variant="auth"
              className={classNames(styles.loginBtn, styles.google)}
              onClick={() => vm.tappedGoogleLoginButton()}
              startIcon="/icons/login-google.svg"
              fullWidth
            >
              Continue with Google
            </Button>
            <Button
              variant="auth"
              className={classNames(styles.loginBtn, styles.microsoft)}
              onClick={() => vm.tappedMicrosoftLoginButton()}
              startIcon="/icons/login-microsoft.svg"
              fullWidth
            >
              Continue with Office 365
            </Button>
            {vm.errorMessage && (
              <div className={styles.loginFail}>
                <Text variant="subheader2" as="h2">
                  {vm.errorMessage}
                </Text>
                <p>Previously signed into Ampersand with a different email?</p>
                <p>
                  Contact us at{" "}
                  <FeedbackMailTo
                    className={styles.mailto}
                    pageTitle="Login"
                    subject="Update login email"
                  />{" "}
                  to update.
                </p>
              </div>
            )}
          </div>
          <Text as="h2" variant="subheader1" className={styles.notYet}>
            Not yet a Sequoia Founder?
          </Text>
          <Text
            as="a"
            href="https://www.sequoiacap.com/article/writing-a-business-plan/"
            variant="subheader3"
            color="textSupporting"
            underline
          >
            Learn how to become one
          </Text>
        </div>
      </div>
    </>
  );
}
LoginContainer.whyDidYouRender = true;

export default LoginContainer;

// -------------------- Private elements ---------------

function SquidFutureFounder(props: SquidItemProps) {
  return (
    <Squid.Item colorize {...props}>
      <a
        className={styles.futureFounder}
        href="https://www.sequoiacap.com/article/writing-a-business-plan/"
      >
        <Text variant="label1" className={styles.type}>
          Future Founders
        </Text>
        <div className={styles.name}>Learn the best way to pitch to us</div>
        <img src="/icons/right-arrow.svg" />
      </a>
    </Squid.Item>
  );
}
